import { useState, useEffect, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Plus } from 'lucide-react';
import { getPromptTemplateCases, createPromptTemplateCase, deletePromptTemplateCase } from '../api';
import type { PromptTemplateCaseListing } from '../api';
import { PrompthubNav, CaseFormModal, CaseDetailModal, DeleteConfirmDialog } from '../components';
import { PageShell, Button as ShellButton } from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import styles from './PrompthubPage.module.scss';

export default function PromptCasesPage() {
  const [cases, setCases] = useState<PromptTemplateCaseListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplateCaseListing | null>(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPromptTemplateCases();
      setCases(res.items ?? []);
    } catch {
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const handleCreateSubmit = useCallback(
    async (form: { title: string; description?: string; role_ids: string[] }) => {
      await createPromptTemplateCase(form);
      await loadCases();
      setCreateModalOpen(false);
    },
    [loadCases]
  );

  const handleCaseClick = useCallback((item: PromptTemplateCaseListing) => {
    setSelectedCaseId(item.id);
    setDetailModalOpen(true);
  }, []);

  const handleEditClick = useCallback((e: React.MouseEvent, item: PromptTemplateCaseListing) => {
    e.stopPropagation();
    setSelectedCaseId(item.id);
    setDetailModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((e: React.MouseEvent, item: PromptTemplateCaseListing) => {
    e.stopPropagation();
    setDeleteTarget(item);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    await deletePromptTemplateCase(deleteTarget.id);
    await loadCases();
    if (selectedCaseId === deleteTarget.id) {
      setDetailModalOpen(false);
      setSelectedCaseId(null);
    }
    setDeleteTarget(null);
  }, [deleteTarget, selectedCaseId, loadCases]);

  const handleDetailClose = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedCaseId(null);
  }, []);

  const casesList = (
    <section className={styles.catalogSection}>
      {!USE_NEW_SHELL && (
        <div className={styles.headerRow}>
          <h2 className={styles.casesHeader}>Кейсы шаблонов промптов</h2>
          <button
            type="button"
            className={styles.addButton}
            onClick={() => setCreateModalOpen(true)}
          >
            <AddIcon sx={{ fontSize: 20 }} />
            Создать кейс
          </button>
        </div>
      )}
      {loading ? (
        <div className={styles.loading}>Загрузка…</div>
      ) : cases.length === 0 ? (
        <div className={styles.empty}>
          Нет кейсов. Создайте кейс — набор шаблонов для определённых ролей.
        </div>
      ) : (
        <div className={styles.casesGrid}>
          {cases.map((item) => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              className={styles.caseCard}
              onClick={() => handleCaseClick(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCaseClick(item);
                }
              }}
            >
              <div className={styles.caseCardContent}>
                <h3 className={styles.caseCardTitle}>{item.title}</h3>
                {item.description && <p className={styles.caseCardDesc}>{item.description}</p>}
              </div>
              <div className={styles.caseCardActions}>
                <button
                  type="button"
                  className={styles.caseCardBtn}
                  onClick={(e) => handleEditClick(e, item)}
                  title="Открыть"
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </button>
                <button
                  type="button"
                  className={styles.caseCardBtn}
                  onClick={(e) => handleDeleteClick(e, item)}
                  title="Удалить"
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const modals = (
    <>
      <CaseFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
      />
      <CaseDetailModal
        open={detailModalOpen}
        onClose={handleDetailClose}
        caseId={selectedCaseId}
        onCaseUpdated={loadCases}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Удалить кейс?"
        message={
          deleteTarget
            ? `Кейс «${deleteTarget.title}» будет удалён. Шаблоны останутся в каталоге.`
            : ''
        }
      />
    </>
  );

  if (USE_NEW_SHELL) {
    return (
      <>
        <PageShell
          title="Кейсы промптов"
          subtitle="Наборы шаблонов под определённые роли и сценарии"
          toolbar={<PrompthubNav />}
          actions={
            <ShellButton
              variant="primary"
              leftIcon={<Plus size={14} strokeWidth={2} />}
              onClick={() => setCreateModalOpen(true)}
            >
              Создать кейс
            </ShellButton>
          }
        >
          {casesList}
        </PageShell>
        {modals}
      </>
    );
  }

  return (
    <div className={styles.root}>
      <PrompthubNav />
      {casesList}
      {modals}
    </div>
  );
}
